import React, { useState } from 'react';
import { Checkbox } from '../../../shared/components/Checkbox';
import { Input } from '../../../shared/components/Input';
import { Button } from '../../../shared/components/Button';
import { Select } from '../../../shared/components/Select';
import { ModuleDetailPane } from './modules/ModuleDetailPane';
import { WeldingConfigDrawer, WeldingConfigGroups } from './modules/WeldingConfigDrawer';
import { WeldingBreakdownPanel, WeldingBreakdownDetails } from './modules/WeldingBreakdownPanel';
import { PlatformOptionsPane, PlatformOptions } from './PlatformOptionsPane';
import { getModuleDef, isModuleKey, ModuleState } from './modules/moduleDefs';
import { SelectedProduct } from '../types';
import { ModuleBreakdown } from '../utils/pricingSheetData';

// Rail id for the project-level Platform Subscription item (not a real product).
const PLATFORM_TAB = 'platformSubscription';

const SPARE_PRICING_OPTIONS = [
  { value: 'hw_only', label: 'HW only' },
  { value: 'hw_sw', label: 'HW + SW' },
  { value: 'hw_sw_other', label: 'HW + SW + Infrastructure' },
];

interface BaseItem {
  id: string;
  name: string;
}

interface QuantityItem extends BaseItem {
  hasQuantity: true;
  quantityLabel: string;
  modes?: string[];
}

interface SimpleItem extends BaseItem {
  hasQuantity?: false;
}

type ProductItem = SimpleItem | QuantityItem;

interface ProductSection {
  title: string;
  items: ProductItem[];
}

interface Product {
  id: string;
  name: string;
  sections?: ProductSection[];
  items?: ProductItem[];
}

export const PRODUCTS_DATA: { [key: string]: Product } = {
  materialTraceability: {
    id: 'materialTraceability',
    name: 'Material Traceability',
    items: [
      { id: 'pipe', name: 'Pipes' },
      { id: 'gas', name: 'Gas' },
      { id: 'wire', name: 'Wire' },
      { id: 'coating', name: 'Coating materials' }
    ]
  },
  pipeFitter: {
    id: 'pipeFitter',
    name: 'PipeFitter',
    items: [
      { id: 'isometric', name: 'Isometric Drawing Management' },
      { id: 'spoolTracking', name: 'Spool Tracking' },
      { id: 'fitUp', name: 'Fit-Up Management' }
    ]
  },
  welding: {
    id: 'welding',
    name: 'Welding',
    sections: [
      {
        title: 'Products',
        items: [
          { 
            id: 'm500', 
            name: 'M-500',
            hasQuantity: true,
            quantityLabel: 'Quantity',
            modes: ['live', 'offline']
          },
          { 
            id: 'p600z', 
            name: 'P-600z',
            hasQuantity: true,
            quantityLabel: 'Quantity',
            modes: ['live', 'offline']
          },
          { 
            id: 'p625', 
            name: 'P-625',
            hasQuantity: true,
            quantityLabel: 'Quantity',
            modes: ['live', 'offline']
          },
          { 
            id: 'iwm', 
            name: 'IWM',
            hasQuantity: true,
            quantityLabel: 'Quantity',
            modes: ['live', 'offline']
          },
          { 
            id: 'manual', 
            name: 'Manual',
            hasQuantity: true,
            quantityLabel: 'Quantity',
            modes: ['live', 'offline']
          }
        ]
      },
      {
        title: 'Services',
        items: [
          {
            id: 'remoteSupport',
            name: 'Remote Support',
            hasQuantity: true,
            quantityLabel: 'Hours per month'
          },
          {
            id: 'training',
            name: 'Training'
          },
          {
            id: 'provisioning',
            name: 'Provisioning',
            hasQuantity: true,
            quantityLabel: 'Number of hours'
          },
          {
            id: 'mobilizing',
            name: 'Mobilizing'
          },
          {
            id: 'onFieldSupport',
            name: 'On-field Support',
            hasQuantity: true,
            quantityLabel: 'Number of days'
          }
        ]
      }
    ]
  },
  ndt: {
    id: 'ndt',
    name: 'NDT',
    items: [
      { id: 'rtFilm', name: 'RT Film Tracking' },
      { id: 'ndtReport', name: 'NDT Report Management' },
      { id: 'defectTracking', name: 'Defect Tracking' }
    ]
  },
  coating: {
    id: 'coating',
    name: 'Coating',
    items: [
      { id: 'surfacePrep', name: 'Surface Preparation' },
      { id: 'coatingApplication', name: 'Coating Application' },
      { id: 'coatingInspection', name: 'Coating Inspection' },
      { id: 'repairTracking', name: 'Repair Tracking' }
    ]
  }
};

interface ProductSelectorProps {
  selectedProducts: {
    [key: string]: SelectedProduct;
  };
  moduleTotals?: Record<string, number>;
  // Per-module slices of the last Calculate Price result, keyed by module id.
  // Feeds the inline read-only breakdown panel in each module's detail pane.
  productBreakdowns?: Record<string, ModuleBreakdown>;
  // Active configuration's Material and NRE cost blocks, used to prefill the
  // per-proposal cost-assumptions drawer in the Material Traceability pane.
  moduleConfigDefaults?: { material?: Record<string, number>; nre?: Record<string, number> };
  pipefitterConfig?: Record<string, number>;
  ndtConfig?: Record<string, number>;
  coatingConfig?: Record<string, number>;
  // The global welding config groups, prefilling the per-proposal welding
  // configuration drawer, plus the proposal's overrides and a setter. Keys are
  // "group.field" (e.g. "hardware.unitCost").
  weldingConfig?: WeldingConfigGroups;
  weldingOverrides?: Record<string, number>;
  onWeldingOverrideChange?: (key: string, value: number | undefined) => void;
  onWeldingResetAll?: () => void;
  // The last-calculated welding breakdown, shown read-only on the welding tab.
  weldingBreakdown?: WeldingBreakdownDetails;
  // Project-level Platform Subscription: the include toggle, the options that
  // drive its price, and the last-calculated project-level total for the badge.
  includePlatform?: boolean;
  onIncludePlatformChange?: (included: boolean) => void;
  platformOptions?: PlatformOptions;
  onPlatformOptionsChange?: (patch: Partial<PlatformOptions>) => void;
  platformTotal?: number;
  platformConfig?: Record<string, number>;
  platformOverrides?: Record<string, number>;
  onPlatformOverrideChange?: (key: string, value: number | undefined) => void;
  retentionConfig?: Record<string, number>;
  retentionOverrides?: Record<string, number>;
  onRetentionOverrideChange?: (key: string, value: number | undefined) => void;
  platformMonths?: number;
  platformDiscountPct?: number;
  onModuleChange?: (productId: string, patch: Partial<ModuleState>) => void;
  onProductChange: (productId: string, isSelected: boolean) => void;
  onProductItemChange: (productId: string, itemId: string, isSelected: boolean) => void;
  onQuantityChange?: (productId: string, itemId: string, quantity: number) => void;
  onSpareQuantityChange?: (productId: string, itemId: string, spareQuantity: number) => void;
  onUnitRateChange?: (productId: string, itemId: string, unitRate?: number) => void;
  onSparePricingModeChange?: (
    productId: string,
    itemId: string,
    sparePricingMode: 'hw_only' | 'hw_sw' | 'hw_sw_other'
  ) => void;
  onTrainingDurationTypeChange?: (
    productId: string,
    itemId: string,
    trainingDurationType: 'quick' | 'medium' | 'long'
  ) => void;
  onNumPeopleChange?: (productId: string, itemId: string, numPeople: number) => void;
  serviceRateDefaults?: {
    remoteSupportHourlyRate?: number;
    trainingHourlyRate?: number;
    trainingQuickHours?: number;
    trainingMediumHours?: number;
    trainingLongHours?: number;
    provisioningHourlyRate?: number;
    onFieldSupportDailyRate?: number;
    mobilizingShippingCostPerKg?: number;
    defaultSparePricingMode?: 'hw_only' | 'hw_sw' | 'hw_sw_other';
    allowSparePricingModeOverride?: boolean;
  };
  formData?: {
    startDate: string;
    endDate: string;
    duration: {
      value: string;
      unit: 'months' | 'years' | 'days';
    };
    milestones: Array<{
      id: string;
      title: string;
      description: string;
      targetDate: string;
    }>;
  };
}

interface WeldingItem extends QuantityItem {
  modes?: string[];
}

interface ValidationState {
  [key: string]: {
    hasQuantity: boolean;
    hasMode: boolean;
    showError: boolean;
  };
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  selectedProducts,
  onProductChange,
  onProductItemChange,
  onQuantityChange,
  onSpareQuantityChange,
  onUnitRateChange,
  onSparePricingModeChange,
  onTrainingDurationTypeChange,
  onNumPeopleChange,
  serviceRateDefaults,
  formData,
  moduleTotals,
  productBreakdowns,
  moduleConfigDefaults,
  pipefitterConfig,
  ndtConfig,
  coatingConfig,
  weldingConfig,
  weldingOverrides,
  onWeldingOverrideChange,
  onWeldingResetAll,
  weldingBreakdown,
  includePlatform = true,
  onIncludePlatformChange,
  platformOptions,
  onPlatformOptionsChange,
  platformTotal,
  platformConfig,
  platformOverrides,
  onPlatformOverrideChange,
  retentionConfig,
  retentionOverrides,
  onRetentionOverrideChange,
  platformMonths,
  platformDiscountPct,
  onModuleChange
}) => {
  const [activeTab, setActiveTab] = useState<string>(Object.keys(PRODUCTS_DATA)[0]);
  const [weldingConfigOpen, setWeldingConfigOpen] = useState(false);
  const [validation, setValidation] = useState<ValidationState>({});
  const [draftQuantities, setDraftQuantities] = useState<Record<string, string>>({});
  const [draftSpareQuantities, setDraftSpareQuantities] = useState<Record<string, string>>({});
  const [draftUnitRates, setDraftUnitRates] = useState<Record<string, string>>({});
  const activeProduct = PRODUCTS_DATA[activeTab];
  const getQuantityKey = (productId: string, itemId: string) => `${productId}:${itemId}`;
  const getSpareQuantityKey = (productId: string, itemId: string) => `${productId}:${itemId}:spare`;
  const getUnitRateKey = (productId: string, itemId: string) => `${productId}:${itemId}:unitRate`;

  const handleSelectAll = (productId: string, shouldSelect: boolean) => {
    const product = PRODUCTS_DATA[productId];
    
    // First update the product selection state
    onProductChange(productId, shouldSelect);
    
    if (product.sections) {
      // For products with sections (like welding)
      product.sections.forEach(section => {
        section.items.forEach(item => {
          // First select/unselect the base item
          onProductItemChange(productId, item.id, shouldSelect);
          
          // Handle quantity if needed
          const existingQuantity = selectedProducts[productId]?.items[item.id]?.quantity || 0;
          const existingSpareQuantity = selectedProducts[productId]?.items[item.id]?.spareQuantity || 0;
          if ('hasQuantity' in item && item.hasQuantity && shouldSelect && (existingQuantity + existingSpareQuantity) <= 0) {
            onQuantityChange?.(productId, item.id, 1);
          }
          
          // Handle welding modes
          if (productId === 'welding' && section.title === 'Products') {
            if (shouldSelect) {
              // Preserve existing mode choice for already-selected items.
              const existingLive = selectedProducts[productId]?.items[`${item.id}_live`]?.selected || false;
              const existingOffline = selectedProducts[productId]?.items[`${item.id}_offline`]?.selected || false;
              if (!existingLive && !existingOffline) {
                onProductItemChange(productId, `${item.id}_live`, true);
                onProductItemChange(productId, `${item.id}_offline`, true);
              }
            } else {
              // When unselecting, clear both modes
              onProductItemChange(productId, `${item.id}_live`, false);
              onProductItemChange(productId, `${item.id}_offline`, false);
            }
          }
        });
      });
    } else if (product.items) {
      // For products without sections (like Material Traceability)
      product.items.forEach(item => {
        // Select/unselect each item
        onProductItemChange(productId, item.id, shouldSelect);
        
        // Handle quantity if needed
        const existingQuantity = selectedProducts[productId]?.items[item.id]?.quantity || 0;
        const existingSpareQuantity = selectedProducts[productId]?.items[item.id]?.spareQuantity || 0;
        if ('hasQuantity' in item && item.hasQuantity && shouldSelect && (existingQuantity + existingSpareQuantity) <= 0) {
          onQuantityChange?.(productId, item.id, 1);
        }
      });
    }
  };

  const isAllSelected = (productId: string) => {
    const product = PRODUCTS_DATA[productId];
    let totalItems = 0;
    let selectedItems = 0;

    if (product.sections) {
      product.sections.forEach(section => {
        section.items.forEach(item => {
          totalItems++;
          if (selectedProducts[productId]?.items[item.id]?.selected) {
            // For welding products, check both item selection and modes
            if (productId === 'welding' && section.title === 'Products') {
              const hasLive = selectedProducts[productId]?.items[`${item.id}_live`]?.selected;
              const hasOffline = selectedProducts[productId]?.items[`${item.id}_offline`]?.selected;
              const actualQuantity = selectedProducts[productId]?.items[item.id]?.quantity || 0;
              const spareQuantity = selectedProducts[productId]?.items[item.id]?.spareQuantity || 0;
              if ((actualQuantity + spareQuantity) > 0 && hasLive && hasOffline) {
                selectedItems++;
              }
            } else {
              selectedItems++;
            }
          }
        });
      });
    } else if (product.items) {
      product.items.forEach(item => {
        totalItems++;
        if (selectedProducts[productId]?.items[item.id]?.selected) {
          selectedItems++;
        }
      });
    }

    return totalItems > 0 && totalItems === selectedItems;
  };

  const getBaseItemIds = (productId: string) => {
    const product = PRODUCTS_DATA[productId];
    if (!product) return [];

    if (product.sections) {
      return product.sections.flatMap(section => section.items.map(item => item.id));
    }

    return product.items?.map(item => item.id) || [];
  };

  const getSelectedItemsCount = (productId: string) => {
    return getBaseItemIds(productId).filter(
      itemId => selectedProducts[productId]?.items[itemId]?.selected
    ).length;
  };

  const getSortedItems = (productId: string, items: ProductItem[]) => {
    return [...items].sort((a, b) => {
      const aSelected = selectedProducts[productId]?.items[a.id]?.selected || false;
      const bSelected = selectedProducts[productId]?.items[b.id]?.selected || false;

      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  };

  const isSectionAllSelected = (productId: string, section: ProductSection) => {
    return section.items.every(item => selectedProducts[productId]?.items[item.id]?.selected);
  };

  const handleSectionSelectAll = (
    productId: string,
    section: ProductSection,
    shouldSelect: boolean
  ) => {
    if (shouldSelect) {
      onProductChange(productId, true);
    }

    section.items.forEach(item => {
      onProductItemChange(productId, item.id, shouldSelect);

      const existingQuantity = selectedProducts[productId]?.items[item.id]?.quantity || 0;
      const existingSpareQuantity = selectedProducts[productId]?.items[item.id]?.spareQuantity || 0;
      if ('hasQuantity' in item && item.hasQuantity && shouldSelect && (existingQuantity + existingSpareQuantity) <= 0) {
        onQuantityChange?.(productId, item.id, 1);
      }

      if (productId === 'welding' && section.title === 'Products') {
        if (shouldSelect) {
          const existingLive = selectedProducts[productId]?.items[`${item.id}_live`]?.selected || false;
          const existingOffline = selectedProducts[productId]?.items[`${item.id}_offline`]?.selected || false;

          if (!existingLive && !existingOffline) {
            onProductItemChange(productId, `${item.id}_live`, true);
            onProductItemChange(productId, `${item.id}_offline`, true);
          }
        } else {
          onProductItemChange(productId, `${item.id}_live`, false);
          onProductItemChange(productId, `${item.id}_offline`, false);
        }
      }
    });

    if (!shouldSelect) {
      const sectionItemIds = new Set(section.items.map(item => item.id));
      const hasSelectedOutsideSection = getBaseItemIds(productId)
        .filter(itemId => !sectionItemIds.has(itemId))
        .some(itemId => selectedProducts[productId]?.items[itemId]?.selected);

      onProductChange(productId, hasSelectedOutsideSection);
    }
  };

  const getSectionSummary = (productId: string, section: ProductSection) => {
    let selectedCount = 0;
    let incompleteCount = 0;

    section.items.forEach(item => {
      const isSelected = selectedProducts[productId]?.items[item.id]?.selected || false;
      if (!isSelected) return;

      selectedCount++;

      const requiresQuantity = ('hasQuantity' in item && item.hasQuantity) || item.id === 'training';
      const quantity = selectedProducts[productId]?.items[item.id]?.quantity || 0;
      const spareQuantity = selectedProducts[productId]?.items[item.id]?.spareQuantity || 0;
      const effectiveQuantity = productId === 'welding' && section.title === 'Products'
        ? quantity + spareQuantity
        : quantity;
      if (requiresQuantity && effectiveQuantity <= 0) {
        incompleteCount++;
        return;
      }

      if (productId === 'welding' && section.title === 'Products') {
        const hasMode = Boolean(
          selectedProducts[productId]?.items[`${item.id}_live`]?.selected ||
          selectedProducts[productId]?.items[`${item.id}_offline`]?.selected
        );

        if (!hasMode) {
          incompleteCount++;
        }
      }
    });

    return { selectedCount, incompleteCount };
  };

  const handleProductChange = (productId: string, checked: boolean) => {
    // First check if we can unselect
    if (!checked) {
      const hasSelectedItems = Object.values(selectedProducts[productId]?.items || {})
        .some(item => item.selected);
      
      if (hasSelectedItems) {
        // Don't allow unselecting the product if it has selected items
        return;
      }
    }
    
    // Update the product selection state
    onProductChange(productId, checked);
  };

  const handleQuantityChange = (productId: string, itemId: string, value: string) => {
    const key = getQuantityKey(productId, itemId);

    // Allow temporary empty value while editing (e.g. replace "3" with "20")
    if (value === '') {
      const currentSpareQuantity = selectedProducts[productId]?.items[itemId]?.spareQuantity || 0;
      setDraftQuantities(prev => ({ ...prev, [key]: '' }));
      setValidation(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          hasQuantity: currentSpareQuantity > 0,
          showError: false
        }
      }));
      return;
    }

    const quantity = Number(value);
    if (Number.isInteger(quantity) && quantity >= 0 && onQuantityChange) {
      onQuantityChange(productId, itemId, quantity);
      setDraftQuantities(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      const currentSpareQuantity = selectedProducts[productId]?.items[itemId]?.spareQuantity || 0;
      const effectiveQuantity = quantity + currentSpareQuantity;
      setValidation(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          hasQuantity: effectiveQuantity > 0,
          showError: effectiveQuantity > 0 && !prev[itemId]?.hasMode
        }
      }));

      if (effectiveQuantity === 0) {
        onProductItemChange(productId, `${itemId}_live`, false);
        onProductItemChange(productId, `${itemId}_offline`, false);
      }
    }
  };

  const handleQuantityBlur = (productId: string, itemId: string) => {
    const key = getQuantityKey(productId, itemId);
    if (draftQuantities[key] !== '' || !onQuantityChange) return;

    onQuantityChange(productId, itemId, 0);
    const currentSpareQuantity = selectedProducts[productId]?.items[itemId]?.spareQuantity || 0;
    if (currentSpareQuantity === 0) {
      onProductItemChange(productId, `${itemId}_live`, false);
      onProductItemChange(productId, `${itemId}_offline`, false);
    }

    setValidation(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        hasQuantity: currentSpareQuantity > 0,
        showError: false
      }
    }));

    setDraftQuantities(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSpareQuantityChange = (productId: string, itemId: string, value: string) => {
    const key = getSpareQuantityKey(productId, itemId);

    if (value === '') {
      const currentActualQuantity = selectedProducts[productId]?.items[itemId]?.quantity || 0;
      setDraftSpareQuantities(prev => ({ ...prev, [key]: '' }));
      setValidation(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          hasQuantity: currentActualQuantity > 0,
          showError: false
        }
      }));
      return;
    }

    const spareQuantity = Number(value);
    if (Number.isInteger(spareQuantity) && spareQuantity >= 0 && onSpareQuantityChange) {
      onSpareQuantityChange(productId, itemId, spareQuantity);
      setDraftSpareQuantities(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      const currentActualQuantity = selectedProducts[productId]?.items[itemId]?.quantity || 0;
      const effectiveQuantity = currentActualQuantity + spareQuantity;
      setValidation(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          hasQuantity: effectiveQuantity > 0,
          showError: effectiveQuantity > 0 && !prev[itemId]?.hasMode
        }
      }));

      if (effectiveQuantity === 0) {
        onProductItemChange(productId, `${itemId}_live`, false);
        onProductItemChange(productId, `${itemId}_offline`, false);
      }
    }
  };

  const handleSpareQuantityBlur = (productId: string, itemId: string) => {
    const key = getSpareQuantityKey(productId, itemId);
    if (draftSpareQuantities[key] !== '' || !onSpareQuantityChange) return;

    onSpareQuantityChange(productId, itemId, 0);
    const currentActualQuantity = selectedProducts[productId]?.items[itemId]?.quantity || 0;
    if (currentActualQuantity === 0) {
      onProductItemChange(productId, `${itemId}_live`, false);
      onProductItemChange(productId, `${itemId}_offline`, false);
    }

    setValidation(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        hasQuantity: currentActualQuantity > 0,
        showError: false
      }
    }));

    setDraftSpareQuantities(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleModeChange = (productId: string, itemId: string, mode: string, isSelected: boolean) => {
    if (mode === 'live') {
      if (isSelected) {
        // When Live is selected, force Offline to be selected
        onProductItemChange(productId, `${itemId}_offline`, true);
      }
    }
    // Always update the current mode
    onProductItemChange(productId, `${itemId}_${mode}`, isSelected);
    
    // Update validation state when mode changes
    const otherMode = mode === 'live' ? 'offline' : 'live';
    const hasMode = isSelected || selectedProducts[productId]?.items[`${itemId}_${otherMode}`]?.selected;
    
    setValidation(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        hasMode,
        showError: prev[itemId]?.hasQuantity && !hasMode
      }
    }));
  };

  const handleUnitRateChange = (productId: string, itemId: string, value: string) => {
    const key = getUnitRateKey(productId, itemId);

    if (value === '') {
      setDraftUnitRates(prev => ({ ...prev, [key]: '' }));
      onUnitRateChange?.(productId, itemId, undefined);
      return;
    }

    const unitRate = Number(value);
    if (Number.isFinite(unitRate) && unitRate >= 0) {
      onUnitRateChange?.(productId, itemId, unitRate);
      setDraftUnitRates(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const renderWeldingSystem = (item: WeldingItem, productId: string) => {
    const isSelected = selectedProducts[productId]?.items[item.id]?.selected || false;
    const actualQuantity = selectedProducts[productId]?.items[item.id]?.quantity || 0;
    const spareQuantity = selectedProducts[productId]?.items[item.id]?.spareQuantity || 0;
    const totalUnits = actualQuantity + spareQuantity;
    const sparePricingMode = selectedProducts[productId]?.items[item.id]?.sparePricingMode
      ?? serviceRateDefaults?.defaultSparePricingMode
      ?? 'hw_only';
    const allowSpareModeOverride = serviceRateDefaults?.allowSparePricingModeOverride ?? true;
    const quantityKey = getQuantityKey(productId, item.id);
    const actualQuantityValue = quantityKey in draftQuantities
      ? draftQuantities[quantityKey]
      : (actualQuantity || '');
    const spareQuantityKey = getSpareQuantityKey(productId, item.id);
    const spareQuantityValue = spareQuantityKey in draftSpareQuantities
      ? draftSpareQuantities[spareQuantityKey]
      : (spareQuantity || '');
    const isLive = selectedProducts[productId]?.items[`${item.id}_live`]?.selected || false;
    const isOffline = selectedProducts[productId]?.items[`${item.id}_offline`]?.selected || false;
    
    const showValidationError = validation[item.id]?.showError;
    const modesDisabled = totalUnits === 0;
    const sparePricingModeLabel = sparePricingMode === 'hw_sw_other'
      ? 'HW + SW + Infrastructure'
      : sparePricingMode === 'hw_sw'
        ? 'HW + SW'
        : 'HW only';

    return (
      <div
        key={item.id}
        className={`rounded-lg border px-4 py-3 transition-colors ${
          isSelected ? 'border-primary/20 bg-primary/5' : 'border-[#E2E5EA] bg-white'
        }`}
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <Checkbox
            id={`${productId}-${item.id}`}
            checked={isSelected}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onProductItemChange(productId, item.id, e.target.checked)}
            label={item.name}
          />

          {isSelected && (
            <div className="flex flex-col gap-3 md:ml-4 md:gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  value={actualQuantityValue}
                  onChange={(e) => handleQuantityChange(productId, item.id, e.target.value)}
                  onBlur={() => handleQuantityBlur(productId, item.id)}
                  placeholder="0"
                  className="w-24"
                />
                <span className="text-sm text-heritage-grey">actual</span>

                <span className="text-sm text-heritage-grey px-1">+</span>

                <Input
                  type="number"
                  min={0}
                  value={spareQuantityValue}
                  onChange={(e) => handleSpareQuantityChange(productId, item.id, e.target.value)}
                  onBlur={() => handleSpareQuantityBlur(productId, item.id)}
                  placeholder="0"
                  className="w-24"
                />
                <span className="text-sm text-heritage-grey">spares</span>

                <span className="text-sm text-heritage-grey px-1">=</span>
                <span className="text-sm font-medium text-text-grey">{totalUnits} total units</span>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Checkbox
                  id={`${productId}-${item.id}-offline`}
                  checked={isOffline || isLive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleModeChange(productId, item.id, 'offline', e.target.checked)}
                  label="Offline"
                  disabled={modesDisabled || isLive}
                />
                <Checkbox
                  id={`${productId}-${item.id}-live`}
                  checked={isLive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleModeChange(productId, item.id, 'live', e.target.checked)}
                  label="Live"
                  disabled={modesDisabled}
                />

                <span className="text-sm text-heritage-grey px-1">|</span>
                <span className="text-sm text-heritage-grey">Spare Charging</span>
                {allowSpareModeOverride ? (
                  <div className="w-52">
                    <Select
                      value={sparePricingMode}
                      options={SPARE_PRICING_OPTIONS}
                      onChange={(value) =>
                        onSparePricingModeChange?.(
                          productId,
                          item.id,
                          value as 'hw_only' | 'hw_sw' | 'hw_sw_other'
                        )
                      }
                    />
                  </div>
                ) : (
                  <span className="text-sm font-medium text-text-grey">{sparePricingModeLabel}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {isSelected && showValidationError && (
          <div className="mt-2 text-readable-red text-sm">
            Please select at least one mode (Live or Offline)
          </div>
        )}
      </div>
    );
  };

  const renderRegularItem = (item: ProductItem, productId: string) => {
    const isSelected = selectedProducts[productId]?.items[item.id]?.selected || false;
    const quantity = selectedProducts[productId]?.items[item.id]?.quantity;
    const unitRate = selectedProducts[productId]?.items[item.id]?.unitRate;
    const trainingDurationType =
      selectedProducts[productId]?.items[item.id]?.trainingDurationType ?? 'quick';
    const quantityKey = getQuantityKey(productId, item.id);
    const quantityValue = quantityKey in draftQuantities
      ? draftQuantities[quantityKey]
      : (quantity || '');
    const unitRateKey = getUnitRateKey(productId, item.id);
    const hasDraftUnitRate = unitRateKey in draftUnitRates;
    const unitRateValue = unitRateKey in draftUnitRates
      ? draftUnitRates[unitRateKey]
      : (unitRate ?? '');
    const label = item.id === 'training' ? 'Number of people' : 
                 'hasQuantity' in item && item.hasQuantity ? item.quantityLabel : '';
    const isVolatileRateItem = productId === 'welding' && (
      item.id === 'remoteSupport' ||
      item.id === 'training' ||
      item.id === 'provisioning' ||
      item.id === 'onFieldSupport'
    );
    const isMobilizingRateItem = productId === 'welding' && item.id === 'mobilizing';
    const volatileRateLabel = item.id === 'onFieldSupport'
      ? 'Daily Rate'
      : item.id === 'mobilizing'
        ? 'Shipping Cost / kg'
        : 'Hourly Rate';
    const defaultVolatileRate = item.id === 'remoteSupport'
      ? serviceRateDefaults?.remoteSupportHourlyRate
      : item.id === 'training'
        ? serviceRateDefaults?.trainingHourlyRate
        : item.id === 'provisioning'
        ? serviceRateDefaults?.provisioningHourlyRate
        : item.id === 'onFieldSupport'
          ? serviceRateDefaults?.onFieldSupportDailyRate
          : item.id === 'mobilizing'
            ? serviceRateDefaults?.mobilizingShippingCostPerKg
            : undefined;
    const showRateOverrideInput = isVolatileRateItem || isMobilizingRateItem;
    const showTrainingDurationInput = productId === 'welding' && item.id === 'training';
    const trainingQuickHours = serviceRateDefaults?.trainingQuickHours ?? 1;
    const trainingMediumHours = serviceRateDefaults?.trainingMediumHours ?? 4;
    const trainingLongHours = serviceRateDefaults?.trainingLongHours ?? 8;

    return (
      <div
        key={item.id}
        className={`rounded-lg border px-4 py-3 transition-colors ${
          isSelected ? 'border-primary/20 bg-primary/5' : 'border-[#E2E5EA] bg-white'
        }`}
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <Checkbox
            id={`${productId}-${item.id}`}
            checked={isSelected}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onProductItemChange(productId, item.id, e.target.checked)}
            label={item.name}
          />

          {isSelected && ((('hasQuantity' in item && item.hasQuantity) || item.id === 'training') || showRateOverrideInput) && (
            <div className="md:ml-4">
              <div className="flex flex-wrap items-center gap-2">
                {(('hasQuantity' in item && item.hasQuantity) || item.id === 'training') && (
                  <>
                    <Input
                      type="number"
                      min={0}
                      value={quantityValue}
                      onChange={(e) => handleQuantityChange(productId, item.id, e.target.value)}
                      onBlur={() => handleQuantityBlur(productId, item.id)}
                      className="w-32"
                      placeholder="0"
                    />
                    <span className="text-sm text-heritage-grey">{label}</span>
                  </>
                )}
                {showTrainingDurationInput && (
                  <>
                    {(('hasQuantity' in item && item.hasQuantity) || item.id === 'training') && (
                      <span className="text-sm text-heritage-grey px-1">|</span>
                    )}
                    <span className="text-sm text-heritage-grey">Session</span>
                    <div className="w-44">
                      <Select
                        value={trainingDurationType}
                        options={[
                          { value: 'quick', label: `Starter (${trainingQuickHours}h)` },
                          { value: 'medium', label: `Standard (${trainingMediumHours}h)` },
                          { value: 'long', label: `Full Day (${trainingLongHours}h)` },
                        ]}
                        onChange={(value) =>
                          onTrainingDurationTypeChange?.(
                            productId,
                            item.id,
                            value as 'quick' | 'medium' | 'long'
                          )
                        }
                      />
                    </div>
                  </>
                )}
                {showRateOverrideInput && (
                  <>
                    {(('hasQuantity' in item && item.hasQuantity) || item.id === 'training' || showTrainingDurationInput) && (
                      <span className="text-sm text-heritage-grey px-1">|</span>
                    )}
                    <span className="text-sm text-heritage-grey">{volatileRateLabel}</span>
                    <span className="text-sm text-heritage-grey">$</span>
                    <Input
                      type="number"
                      min={0}
                      value={hasDraftUnitRate ? unitRateValue : (unitRate ?? defaultVolatileRate ?? '')}
                      onChange={(e) => handleUnitRateChange(productId, item.id, e.target.value)}
                      className="w-28"
                      placeholder="0"
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const moduleDef = getModuleDef(activeTab);

  const renderContent = () => {
    if (activeTab === PLATFORM_TAB) {
      return (
        <PlatformOptionsPane
          included={includePlatform}
          onIncludedChange={(value) => onIncludePlatformChange?.(value)}
          options={
            platformOptions ?? {
              numberOfUsers: 0,
              retentionTier: null,
              retentionYears: 0,
              customerManagedCloud: false,
            }
          }
          onChange={(patch) => onPlatformOptionsChange?.(patch)}
          total={platformTotal}
          platformConfig={platformConfig}
          platformOverrides={platformOverrides}
          onPlatformOverrideChange={onPlatformOverrideChange}
          retentionConfig={retentionConfig}
          retentionOverrides={retentionOverrides}
          onRetentionOverrideChange={onRetentionOverrideChange}
          months={platformMonths}
          discountPct={platformDiscountPct}
        />
      );
    }

    if (!activeProduct) return null;

    if (moduleDef) {
      const moduleState = (selectedProducts[activeProduct.id] as ModuleState) || { isSelected: false };
      if (!moduleState.isSelected) {
        return (
          <div className="flex items-center justify-center h-48 text-sm text-heritage-grey">
            Enable {moduleDef.name} from the rail to configure its pricing.
          </div>
        );
      }
      return (
        <ModuleDetailPane
          def={moduleDef}
          module={moduleState}
          onChange={(patch) => onModuleChange?.(activeProduct.id, patch)}
          breakdown={productBreakdowns?.[activeProduct.id]}
          materialConfig={moduleConfigDefaults?.material}
          nreConfig={moduleConfigDefaults?.nre}
          pipefitterConfig={pipefitterConfig}
          ndtConfig={ndtConfig}
          coatingConfig={coatingConfig}
        />
      );
    }

    const weldingOverrideCount = Object.keys(weldingOverrides ?? {}).length;
    return (
      <>
        {activeProduct.id === 'welding' && (
          <div className="mb-4 flex items-center justify-end gap-3">
            {weldingOverrideCount > 0 && (
              <span className="text-xs text-primary">
                {weldingOverrideCount} value{weldingOverrideCount === 1 ? '' : 's'} overridden for this proposal
              </span>
            )}
            <Button
              variant="secondary"
              onClick={() => setWeldingConfigOpen(true)}
              className="px-3 py-1.5 border border-[#E2E5EA] rounded-lg hover:bg-page-bg"
            >
              Edit welding configuration
            </Button>
          </div>
        )}
        {activeProduct.id === 'welding' && (
          <WeldingConfigDrawer
            open={weldingConfigOpen}
            onClose={() => setWeldingConfigOpen(false)}
            config={weldingConfig}
            overrides={weldingOverrides ?? {}}
            onOverrideChange={(key, value) => onWeldingOverrideChange?.(key, value)}
            onResetAll={() => onWeldingResetAll?.()}
            durationMonths={platformMonths}
          />
        )}
        {activeProduct.sections ? (
          activeProduct.sections.map((section, index) => {
            const sortedItems = getSortedItems(activeProduct.id, section.items);
            const { selectedCount, incompleteCount } = getSectionSummary(activeProduct.id, section);
            const allSelectedInSection = isSectionAllSelected(activeProduct.id, section);

            return (
              <div key={index} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium font-heading text-blueprint-blue">{section.title}</h3>
                    <span className="text-sm text-heritage-grey">
                      {selectedCount} selected
                      {incompleteCount > 0 ? ` • ${incompleteCount} incomplete` : ''}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => handleSectionSelectAll(activeProduct.id, section, !allSelectedInSection)}
                    className="px-3 py-1.5 border border-[#E2E5EA] rounded-lg hover:bg-page-bg"
                  >
                    {allSelectedInSection ? 'Unselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="space-y-3">
                  {sortedItems.map((item) =>
                    section.title === 'Products'
                      ? renderWeldingSystem(item as WeldingItem, activeProduct.id)
                      : renderRegularItem(item, activeProduct.id)
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <>
            <div className="mb-4">
              <Button
                variant="secondary"
                onClick={() => handleSelectAll(activeProduct.id, !isAllSelected(activeProduct.id))}
                className="px-4 py-2 border border-[#E2E5EA] rounded-lg hover:bg-page-bg"
              >
                {isAllSelected(activeProduct.id) ? 'Unselect All' : 'Select All'}
              </Button>
            </div>
            <div className="space-y-3">
              {getSortedItems(activeProduct.id, activeProduct.items || []).map((item) =>
                renderRegularItem(item, activeProduct.id)
              )}
            </div>
          </>
        )}
        {activeProduct.id === 'welding' && (
          <div className="mt-6">
            <WeldingBreakdownPanel details={weldingBreakdown} />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex">
      {/* Vertical Tabs */}
      <div className="w-64 border-r border-[#E2E5EA] pr-4">
        <nav className="space-y-1">
          {Object.values(PRODUCTS_DATA).map((product) => {
            const isSelected = selectedProducts[product.id]?.isSelected || false;
            const selectedItemsCount = getSelectedItemsCount(product.id);
            // The rail badge shows the calculated total: per-module totals for the
            // cost-model modules, and the welding total once it is priced. Welding
            // falls back to its selected-item count only before it is calculated.
            const moduleTotal = isModuleKey(product.id)
              ? moduleTotals?.[product.id]
              : product.id === 'welding'
                ? weldingBreakdown?.totalPrice
                : undefined;

            return (
              <button
                key={product.id}
                onClick={() => setActiveTab(product.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === product.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-text-grey hover:bg-page-bg'
                }`}
              >
                <div className="flex items-center">
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handleProductChange(product.id, e.target.checked)}
                    className="mr-3"
                  />
                  <span>{product.name}</span>
                </div>
                {moduleTotal !== undefined && moduleTotal > 0 ? (
                  <span className="ml-2 bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-full">
                    ${Math.round(moduleTotal).toLocaleString()}
                  </span>
                ) : selectedItemsCount > 0 && !isModuleKey(product.id) ? (
                  <span className="ml-2 bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-full">
                    {selectedItemsCount}
                  </span>
                ) : null}
              </button>
            );
          })}

          {/* Project-level Platform Subscription: a checkbox toggle plus its
              detail pane, separate from the per-module products above. */}
          <button
            key={PLATFORM_TAB}
            onClick={() => setActiveTab(PLATFORM_TAB)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === PLATFORM_TAB
                ? 'bg-primary/10 text-primary'
                : 'text-text-grey hover:bg-page-bg'
            }`}
          >
            <div className="flex items-center">
              <Checkbox
                checked={includePlatform}
                onChange={(e) => onIncludePlatformChange?.(e.target.checked)}
                className="mr-3"
              />
              <span>Platform Subscription</span>
            </div>
            {includePlatform && platformTotal !== undefined && platformTotal > 0 ? (
              <span className="ml-2 bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-full">
                ${Math.round(platformTotal).toLocaleString()}
              </span>
            ) : null}
          </button>
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 pl-6">
        <div className="space-y-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
